import { Persona } from '@cibo/core'
import { ClickableCard } from '@cibo/ui'
import RadioButtonUncheckedRounded from '@mui/icons-material/RadioButtonUncheckedRounded'
import TaskAltRounded from '@mui/icons-material/TaskAltRounded'
import { Box, CardContent, CardHeader, Grid, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

export type PersonaCardsProps = {
  personas: Persona[]
  activePersona?: Persona
  onClick?: (persona: Persona) => void
  disabled?: boolean
}

const PERSONA_TOPICS: Record<Persona, string[]> = {
  grower: ['programs', 'fields'],
  advisor: ['programs', 'growers', 'fields'],
  admin: ['programs', 'fields', 'organizations', 'reporting'],
}

export const PersonaCards = ({
  personas,
  activePersona,
  onClick,
  disabled = false,
}: PersonaCardsProps) => {
  const { t } = useTranslation('@cibo/profile/PersonaCards')

  return (
    <Stack spacing={1}>
      <Typography color="text.secondary">{t('appExperience')}</Typography>
      <Box>
        <Grid container spacing={2}>
          {personas.map((persona, idx) => (
            <Grid key={persona} xs={12} sm={6} md={4} lg={3} container item>
              <ClickableCard
                onClick={disabled ? undefined : () => !!onClick && onClick(persona)}
                disabled={disabled}
                sx={{
                  backgroundColor: activePersona === persona ? 'primary.light' : undefined,
                  color: activePersona === persona ? 'primary.contrastText' : 'text.secondary',
                  flex: 1,
                  '.MuiTypography-root': {
                    color: 'inherit',
                  },
                  '.MuiSvgIcon-root': {
                    color:
                      activePersona === persona
                        ? 'inherit'
                        : disabled
                        ? 'text.disabled'
                        : undefined,
                  },
                }}
              >
                <CardHeader
                  title={t('persona', { context: persona })}
                  titleTypographyProps={{
                    variant: 'body1',
                  }}
                  subheader={t('description', { context: persona })}
                  subheaderTypographyProps={{
                    variant: 'caption',
                  }}
                  action={
                    activePersona === persona ? <TaskAltRounded /> : <RadioButtonUncheckedRounded />
                  }
                />

                <CardContent>
                  {PERSONA_TOPICS[persona].map(topic => (
                    <Typography key={topic} variant="body2">
                      &#x2022;&nbsp; {t('topic', { context: topic })}
                    </Typography>
                  ))}
                </CardContent>
              </ClickableCard>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Stack>
  )
}
